import React, {ReactNode} from "react"
import moment from "moment-timezone"
import {BaseLoading} from "components"
import {formatDateOnly, roundTwoDigits} from "helpers"
import styles from "./CheckInCheckOutView.module.css"
import {CheckInCheckOutStudent, getCheckInCheckOutPeriodCount, isCheckInCheckOutAbsent} from "./checkInCheckOutReport"

const formatEventTime = (value: string | null | undefined, timezone: string, timeFormat: string) => {
    if (!value) return ""
    return moment(value).tz(timezone).format(timeFormat)
}

type Props = {
    students: CheckInCheckOutStudent[]
    isLoading: boolean
    timeFormat: string
    dateFormat?: string
    reportHeader?: ReactNode
}

const CheckInCheckOutView = ({students, isLoading, timeFormat, dateFormat, reportHeader}: Props) => {
    const periodCount = getCheckInCheckOutPeriodCount(students)
    const periodIndexes = Array.from({length: periodCount}, (_, index) => index)
    const showDate = !!dateFormat && students.some((student) => !!student.date)
    const columnCount = periodCount * 2 + (showDate ? 6 : 5)

    return (
        <div className={styles.container}>
            <table className={styles.table} style={{width: "100%"}}>
                <thead>
                    {reportHeader ? (
                        <tr>
                            <td colSpan={columnCount} className={styles.reportHeaderCell}>
                                {reportHeader}
                            </td>
                        </tr>
                    ) : null}
                    <tr className={styles.headerRow}>
                        <th className={styles.th} style={{verticalAlign: "bottom"}}>
                            User Id
                        </th>
                        <th className={styles.th} style={{verticalAlign: "bottom", textAlign: "left"}}>
                            First Name
                        </th>
                        <th className={styles.th} style={{verticalAlign: "bottom", textAlign: "left"}}>
                            Last Name
                        </th>
                        {showDate ? <th className={styles.th}>Date</th> : null}
                        {periodIndexes.map((index) => (
                            <React.Fragment key={`period-header-${index}`}>
                                <th className={styles.th}>Check-In</th>
                                <th className={styles.th}>Check-Out</th>
                            </React.Fragment>
                        ))}
                        <th className={styles.th}>Hours</th>
                        <th className={styles.th}>Missed Hours</th>
                    </tr>
                </thead>
                <tbody>
                    {students.map((student, index) => {
                        const timezone = student.timezone || moment.tz.guess()
                        const periods = student.periods ?? []
                        const isAbsent = isCheckInCheckOutAbsent(student)

                        return (
                            <tr key={`${student.profileId ?? "row"}-${student.date ?? ""}-${index}`}>
                                <td className={styles.td} style={{textAlign: "left"}}>
                                    {student.customUserId}
                                </td>
                                <td className={styles.td} style={{textAlign: "left"}}>
                                    {student.firstName}
                                </td>
                                <td className={styles.td} style={{textAlign: "left"}}>
                                    {student.lastName}
                                </td>
                                {showDate ? (
                                    <td className={styles.td}>{formatDateOnly(student.date, dateFormat)}</td>
                                ) : null}
                                {isAbsent ? (
                                    <td className={styles.absent} colSpan={periodCount * 2}>
                                        Absent
                                    </td>
                                ) : (
                                    periodIndexes.map((periodIndex) => {
                                        const period = periods[periodIndex]
                                        return (
                                            <React.Fragment key={`period-${index}-${periodIndex}`}>
                                                <td className={styles.td}>
                                                    {formatEventTime(period?.checkInAt, timezone, timeFormat)}
                                                </td>
                                                <td className={styles.td}>
                                                    {formatEventTime(period?.checkOutAt, timezone, timeFormat)}
                                                </td>
                                            </React.Fragment>
                                        )
                                    })
                                )}
                                <td className={styles.td}>{roundTwoDigits(Number(student.attendedHours ?? 0))}</td>
                                <td className={styles.td}>{roundTwoDigits(Number(student.missedHours ?? 0))}</td>
                            </tr>
                        )
                    })}
                    {students.length === 0 && (
                        <tr>
                            <td colSpan={columnCount} className={styles.td} style={{textAlign: "center"}}>
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {isLoading ? <BaseLoading isShow /> : null}
        </div>
    )
}

export default CheckInCheckOutView
